/**
 * ============================================
 * CRITICAL ALARM MODAL
 * ============================================
 * 
 * Full-screen modal with flashing warning for critical alerts.
 */

import { useEffect, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { useAlerts } from '@/context/AlertContext';
import { Button } from '@/components/ui/Button';
import { ALERT_INFO } from '@/config';

export function CriticalAlarmModal() {
  const { state, dismissAlarm } = useAlerts();
  const [isFlashing, setIsFlashing] = useState(true);
  
  const criticalAlerts = state.activeAlerts.filter(
    a => a.severity === 'critical' && !a.acknowledged
  );
  
  const showModal = state.isAlarmActive && criticalAlerts.length > 0;

  // Flash effect
  useEffect(() => {
    if (!showModal) return;
    const interval = setInterval(() => setIsFlashing(prev => !prev), 500);
    return () => clearInterval(interval);
  }, [showModal]);

  if (!showModal) return null;

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center transition-colors duration-300 ${
      isFlashing ? 'bg-red-900/90' : 'bg-red-950/95'
    }`}>
      <div className="max-w-lg w-full mx-4 text-center">
        {/* Icon */}
        <div className="mb-6">
          <BellRing className={`h-24 w-24 mx-auto text-white ${isFlashing ? 'animate-bounce' : ''}`} />
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-white mb-4">
          🚨 CRITICAL ALERT
        </h1>

        {/* Alert Details */}
        <div className="space-y-3 mb-8">
          {criticalAlerts.map(alert => {
            const info = ALERT_INFO[alert.type];
            const icon = info?.icon ?? '🚨';
            return (
              <div key={alert.id} className="bg-black/30 rounded-lg p-4 text-left">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{icon}</span>
                  <div>
                    <h3 className="text-white font-semibold">{alert.title}</h3>
                    <p className="text-red-200 text-sm">{alert.message}</p>
                    <p className="text-red-300/70 text-xs mt-1">
                      {alert.environmentName} • {alert.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Cancel Button */}
        <Button
          onClick={dismissAlarm}
          className="h-16 px-12 text-xl font-bold bg-white text-red-600 hover:bg-red-100 shadow-lg"
        >
          <X className="h-8 w-8 mr-3" />
          CANCEL ALARM
        </Button>

        <p className="text-red-200/70 text-sm mt-4">
          Click to acknowledge and silence the alarm
        </p>
      </div>
    </div>
  );
}
